import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CreditCardIcon from '@/assets/all-icons/credit-card-icon';
import usePaymentProfile from '@/hooks/usePaymentProfile';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import paymentProfileService from '@/services/paymentProfile.service';
import SupportService from '@/services/support.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import BillingDetailsCard from '@/shared-components/payment-profile/BillingDetailsCard';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolvePaymentCountryHint } from '@/utils/paymentCountry.utils';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import { SUPPORT_TITLE } from './constants';

import './support.css';

/**
 * External supporter popup. This component focuses on clarity and minimal friction.
 */
export default function SupportPublicPopup({
  open,
  onClose,
  config,
  supportedUser = null,
  onConfirm = null,
  isSubmitting = false,
  errorMessage = null,
}) {
  const t = useTranslations('common.profile.buttons.support.public');
  const [selectedPresetIndex, setSelectedPresetIndex] = useState(null);
  const [customSelected, setCustomSelected] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [giveAnonymously, setGiveAnonymously] = useState(false);
  const [pitchExpanded, setPitchExpanded] = useState(false);
  const [pitchOverflowing, setPitchOverflowing] = useState(false);
  const [selectedPerkId, setSelectedPerkId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const customAmountInputRef = useRef(null);
  const contentRef = useRef(null);
  const pitchRef = useRef(null);
  const [paymentMethodsVersion, setPaymentMethodsVersion] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [vatRate, setVatRate] = useState(0);
  const [vatLoading, setVatLoading] = useState(false);
  const [vatError, setVatError] = useState(null);
  const currencyCode = (config?.currency ?? "eur").toUpperCase();
  const currencySymbol = config?.currencySymbol
    ?? (currencyCode === "USD" ? "$" : currencyCode === "GBP" ? "£" : "€");
  const displayTitle = SUPPORT_TITLE;
  const paymentProfile = usePaymentProfile();
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const bidderBillingAddress = paymentProfile?.profile?.billing_address ?? null;
  const bidderBillingInfo = paymentProfile?.profile?.billing_info ?? null;
  const [billingAddress, setBillingAddress] = useState(() => bidderBillingAddress);
  const [billingInfo, setBillingInfo] = useState(() => bidderBillingInfo);
  const buyerCountryHint = resolvePaymentCountryHint({ profile: paymentProfile?.profile, billingAddress });
  const billingVatFingerprint = useMemo(() => ([
    billingInfo?.is_business === true ? "1" : "0",
    typeof billingInfo?.vat_id === "string" ? billingInfo.vat_id.trim().toUpperCase() : "",
    billingInfo?.vat_valid === true ? "1" : "0",
  ].join("|")), [billingInfo?.is_business, billingInfo?.vat_id, billingInfo?.vat_valid]);
  const supportedUserId = supportedUser?._id ?? null;
  const supportedUserProfileImage = resolveUserProfileImage(
    supportedUser?.user_image,
    supportedUser?.userProfileImage,
    supportedUser?.profile_image,
    supportedUser?.image,
  );
  const supportedUserProfileType = resolveUserProfileTypeWithFallback(
    'collector',
    supportedUser?.user_type,
    supportedUser?.__t,
    supportedUser?.type,
    supportedUser?.typeOfAccount,
    supportedUser?.type_of_account,
  );
  const { stateHandler } = useStateHandler();

  function normalizeMethod(candidate, fallbackDefault) {
    if (!candidate || typeof candidate !== 'object') {
      return null;
    }

    const cardDetails =
      candidate.card ||
      candidate.payment_method_details?.card ||
      candidate.payment_method?.card ||
      candidate.details?.card ||
      candidate.billing_details?.card ||
      null;

    const id =
      candidate.id ||
      candidate.payment_method_id ||
      candidate.payment_method ||
      candidate.stripe_payment_method_id ||
      candidate._id ||
      null;

    if (!id) {
      return null;
    }

    const brand = cardDetails?.brand ?? candidate.brand ?? candidate.card_brand ?? t('labels.card');
    const last4 = String(cardDetails?.last4 ?? candidate.last4 ?? candidate.card_last4 ?? '').slice(-4);
    const country = (cardDetails?.country ?? candidate.country ?? candidate.card_country ?? "").toUpperCase();

    return {
      id,
      label: candidate.nickname ?? candidate.label ?? brand,
      details: candidate.details ?? null,
      last4,
      brand,
      isDefault:
        Boolean(candidate.is_default ?? candidate.isDefault ?? candidate.default) ||
        (fallbackDefault ? id === fallbackDefault : false),
      country: country || null,
    };
  }

  const tiers = Array.isArray(config?.tiers) ? config.tiers : [];
  const displayTiers = useMemo(
    () =>
      tiers.filter(tier => {
        const hasLabel = tier.label && tier.label.trim();
        const hasNote = tier.note && tier.note.trim();
        const numericAmount = Number(tier.amount);
        return hasLabel || hasNote || (!Number.isNaN(numericAmount) && numericAmount >= 1);
      }),
    [tiers]
  );

  const presetAmounts = useMemo(() => {
    if (!Array.isArray(config?.defaultAmounts)) {
      return [];
    }

    return config.defaultAmounts
      .filter(slot => !slot?.isCustom)
      .map(slot => Number(slot.amount))
      .filter(amount => Number.isFinite(amount) && amount >= 3)
      .slice(0, 3);
  }, [config?.defaultAmounts]);

  const hasCustomSlot = useMemo(
    () => Array.isArray(config?.defaultAmounts) && config.defaultAmounts.some(slot => slot?.isCustom),
    [config?.defaultAmounts]
  );

  const goalAmount = config?.goal?.amount ? Number(config.goal.amount) : null;
  const goalProgressRaw = config?.goal?.currentAmount ?? config?.goal?.current ?? config?.goal?.progress ?? 0;
  const goalProgress = Number.isFinite(Number(goalProgressRaw)) ? Number(goalProgressRaw) : 0;
  const goalProgressPercentage =
    goalAmount && goalAmount > 0 ? Math.min(100, Math.max(0, (goalProgress / goalAmount) * 100)) : 0;
  const showPitchToggle = pitchOverflowing || pitchExpanded;

  const selectedAmount = useMemo(() => {
    if (customSelected) {
      if (customAmount) {
        const numericValue = Number(customAmount);
        if (!Number.isNaN(numericValue)) {
          return Math.max(0, numericValue);
        }
      }
      return 0;
    }

    if (selectedPresetIndex !== null && presetAmounts[selectedPresetIndex] !== undefined) {
      return presetAmounts[selectedPresetIndex];
    }

    return 0;
  }, [customAmount, customSelected, presetAmounts, selectedPresetIndex]);

  const selectedPaymentMethod = useMemo(
    () =>
      paymentMethods.find(
        method =>
          method.id === selectedPaymentMethodId ||
          method.token === selectedPaymentMethodId ||
          method.key === selectedPaymentMethodId
      ) ?? null,
    [paymentMethods, selectedPaymentMethodId]
  );
  const selectedPaymentMethodStripeId = selectedPaymentMethod?.id ?? null;

  useEffect(() => {
    let isActive = true;
    const numericAmount = Number(selectedAmount);
    if (!supportedUserId || !Number.isFinite(numericAmount) || numericAmount <= 0) {
      setVatAmount(0);
      setVatRate(0);
      setVatError(null);
      setVatLoading(false);
      return;
    }

    setVatLoading(true);
    setVatError(null);

    SupportService.calculateVat(supportedUserId, {
      amount: numericAmount,
      paymentMethodId: selectedPaymentMethodStripeId ?? undefined,
      buyerCountry: buyerCountryHint ?? undefined,
    })
      .then(({ response, data }) => {
        if (!isActive) {
          return;
        }

        if (!response?.ok) {
          throw new Error(data?.message ?? t('errors.unable_to_calculate_vat'));
        }

        setVatRate(Number(data?.vatRate) || 0);
        setVatAmount(Number(data?.vatAmount) || 0);
      })
      .catch(error => {
        if (!isActive) {
          return;
        }
        setVatRate(0);
        setVatAmount(0);
        setVatError(error?.message ?? t('errors.unable_to_calculate_vat'));
      })
      .finally(() => {
        if (isActive) {
          setVatLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    billingVatFingerprint,
    buyerCountryHint,
    selectedAmount,
    supportedUserId,
    selectedPaymentMethodStripeId,
  ]);

  const customAmountNumeric = Number(customAmount);
  const showInlineMinAmountError =
    customSelected &&
    customAmount &&
    Number.isFinite(customAmountNumeric) &&
    customAmountNumeric > 0 &&
    customAmountNumeric < 3;

  const minAmountErrorMessage =
    customSelected && selectedAmount > 0 && selectedAmount < 3
      ? t('errors.min_amount', { currencySymbol })
      : null;
  const displayErrorMessage = errorMessage || minAmountErrorMessage;
  const isCustomAmountActive = customSelected && selectedPerkId === null;
  const currencySymbolPlacement = currencyCode === 'EUR' ? 'suffix' : 'prefix';
  const customAmountInputWidth = `${Math.max(String(customAmount || '0').length, 1)}ch`;

  useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;
    setLoadingPaymentMethods(true);
    setPaymentMethodError(null);

    (async () => {
      try {
        const { response, data } = await paymentProfileService.listPaymentMethods();
        if (!response?.ok) {
          throw new Error(t('errors.payment_methods_load_failed'));
        }

        const list =
          (Array.isArray(data?.payment_methods) && data.payment_methods) ||
          (Array.isArray(data?.data) && data.data) ||
          (Array.isArray(data?.results) && data.results) ||
          (Array.isArray(data?.items) && data.items) ||
          [];

        const normalized = list
          .map(item => normalizeMethod(item, data?.default_payment_method_id ?? data?.default_payment_method))
          .filter(Boolean);

        if (isMounted) {
          setPaymentMethods(normalized);
          setLoadingPaymentMethods(false);
        }
      } catch (error) {
        if (isMounted) {
          setPaymentMethodError(error?.message ?? t('errors.payment_methods_load_failed'));
          setLoadingPaymentMethods(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [open, paymentMethodsVersion]);

  useEffect(() => {
    if (open) {
      setBillingAddress(bidderBillingAddress ?? null);
      setBillingInfo(bidderBillingInfo ?? null);
    }
  }, [bidderBillingAddress, bidderBillingInfo, open]);

  useEffect(() => {
    if (!paymentMethods.length) {
      return;
    }
    const hasSelected = paymentMethods.some(method => method.id === selectedPaymentMethodId);
    if (hasSelected) {
      return;
    }
    const fallback =
      paymentMethods.find(method => method.isDefault)?.id ?? paymentMethods[0]?.id ?? null;
    if (fallback) {
      setSelectedPaymentMethodId(fallback);
    }
  }, [paymentMethods, selectedPaymentMethodId]);

  const vatPercentLabel = Number.isFinite(vatRate) ? vatRate * 100 : 0;
  const totalAmount = selectedAmount + vatAmount;
  const ctaAmount = showPaymentDetails ? totalAmount : selectedAmount;
  const ctaAmountDigits = showPaymentDetails ? 2 : 0;
  const frequencyLabel = frequency === 'monthly' ? t('summary.recurrent') : t('summary.one_time');
  const formatCurrency = (value, digits = 2) => formatCurrencyAmount(value, currencyCode, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }) || '';
  const apiCurrency = currencyCode.toLowerCase();

  function handleSelectPreset(index) {
    if (presetAmounts[index] === undefined) {
      return;
    }

    setSelectedPresetIndex(index);
    setCustomSelected(false);
    setCustomAmount(String(presetAmounts[index]));
    setSelectedPerkId(null);
  }

  function handleCustomAmountFocus() {
    setSelectedPresetIndex(null);
    setCustomSelected(true);
    setSelectedPerkId(null);
  }

  function handleCustomAmountChange(event) {
    const { value } = event.target;
    if (value === '') {
      setCustomAmount('');
    } else {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) {
        setCustomAmount(value);
      }
    }
    setSelectedPresetIndex(null);
    setCustomSelected(true);
    setSelectedPerkId(null);
  }

  function handlePerkSelect(amount, tierId = null) {
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      return;
    }

    setSelectedPresetIndex(null);
    setCustomSelected(true);
    setCustomAmount(String(numericAmount));
    setSelectedPerkId(tierId);
  }

  function resetState({ autoSelect = false } = {}) {
    const shouldSelectPreset = autoSelect && presetAmounts.length > 0;
    if (shouldSelectPreset) {
      setSelectedPresetIndex(0);
      setCustomSelected(false);
      setCustomAmount(String(presetAmounts[0]));
    } else {
      setSelectedPresetIndex(null);
      setCustomSelected(true);
      setCustomAmount('');
    }
    setFrequency('monthly');
    setGiveAnonymously(false);
    setShowPaymentDetails(false);
    setSelectedPerkId(null);
    setPitchExpanded(false);
    setPitchOverflowing(false);
  }

  function handleClose() {
    resetState();
    onClose?.();
  }

  async function handleConfirm() {
    if (selectedAmount < 3 || !selectedPaymentMethod) {
      return;
    }

    if (typeof onConfirm === "function") {
      await onConfirm({
        amount: selectedAmount,
        frequency,
        giveAnonymously,
        vatRate,
        vatAmount,
        total: totalAmount,
        currency: apiCurrency,
        paymentMethodId: selectedPaymentMethod?.id ?? null,
        buyerCountry: buyerCountryHint ?? null,
        billingAddress: billingAddress ?? null,
        billingInfo: billingInfo ?? null,
        supportedUser,
      });
    }
  }

  function blurActiveElement() {
    if (typeof document === "undefined") {
      return;
    }
    const active = document.activeElement;
    if (active && typeof active.blur === "function") {
      active.blur();
    }
  }

  async function handlePrimaryAction() {
    if (isSubmitting) {
      return;
    }
    blurActiveElement();
    if (!showPaymentDetails) {
      setShowPaymentDetails(true);
      return;
    }
    await handleConfirm();
  }

  const handleAddPaymentMethod = useCallback(async () => {
    if (typeof ensurePaymentProfile !== "function") {
      console.warn("[SupportPublicPopup] ensurePaymentProfile missing");
      return;
    }
    setAddingPaymentMethod(true);
    try {
      await ensurePaymentProfile({
        forceSetup: true,
        countryHint: buyerCountryHint ?? undefined,
      });
      setPaymentMethodsVersion((value) => value + 1);
      stateHandler?.set("temporalPopup", {
        text: t('messages.payment_method_added'),
      });
    }
    catch (error) {
      if (error?.code !== "BIDDER_PROFILE_CANCELLED") {
        console.error("[SupportPublicPopup] Unable to add payment method", error);
        stateHandler?.set("temporalPopup", {
          text: t('errors.unable_to_add_payment_method'),
        });
      }
    }
    finally {
      setAddingPaymentMethod(false);
    }
  }, [buyerCountryHint, ensurePaymentProfile, stateHandler]);

  useEffect(() => {
    if (open) {
      resetState({ autoSelect: true }); // Pre-select the first quick option when available.
    }
  }, [open, presetAmounts]);

  useEffect(() => {
    if (!showPaymentDetails || !contentRef.current) return;
    const container = contentRef.current;
    const target = container.scrollHeight - container.clientHeight;
    container.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
  }, [showPaymentDetails]);

  useEffect(() => {
    if (
      selectedPresetIndex !== null &&
      (selectedPresetIndex < 0 || selectedPresetIndex >= presetAmounts.length)
    ) {
      const hasPreset = presetAmounts.length > 0;
      if (hasPreset) {
        setSelectedPresetIndex(0);
        setCustomSelected(false);
        setCustomAmount(String(presetAmounts[0]));
      } else {
        setSelectedPresetIndex(null);
        setCustomSelected(true);
        setCustomAmount('');
      }
    }
  }, [presetAmounts, selectedPresetIndex]);

  useEffect(() => {
    if (presetAmounts.length === 0 && !customSelected) {
      setSelectedPresetIndex(null);
      setCustomSelected(true);
      setCustomAmount('');
    }
  }, [presetAmounts, customSelected]);

  useEffect(() => {
    if (
      selectedPresetIndex !== null &&
      presetAmounts[selectedPresetIndex] !== undefined
    ) {
      setCustomAmount(String(presetAmounts[selectedPresetIndex]));
    }
  }, [presetAmounts, selectedPresetIndex]);

  useEffect(() => {
    if (!open) {
      setPitchExpanded(false);
      setPitchOverflowing(false);
    }
  }, [open]);

  useEffect(() => {
    setPitchExpanded(false);
  }, [config?.pitch]);

  useEffect(() => {
    const el = pitchRef.current;
    if (!el) {
      setPitchOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      setPitchOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }

    return undefined;
  }, [config?.pitch, pitchExpanded, open]);

  return (
    <GenericPopup open={open} onBackgroundClick={handleClose} className="support-public-popup remove-scrollbar" opacity>
      <div className="support-public">
        <header className="support-public__header">
          <h2 className="support-public__title">{displayTitle}</h2>
          <XIcon className="support-public__close" onClick={handleClose} />
        </header>

        <div className="support-public__content" ref={contentRef}>
          {config?.backgroundImage?.previewUrl && (
            <div
              className="support-public__hero"
              style={{ backgroundImage: `url(${config.backgroundImage.previewUrl})` }}
            />
          )}

          {config?.pitch && (
            <div className="support-public__pitch-wrapper">
              <p
                ref={pitchRef}
                className={`support-public__pitch${pitchExpanded ? ' support-public__pitch--expanded' : ' support-public__pitch--collapsed'}`}
              >
                {config.pitch}
              </p>
              {showPitchToggle && (
                <button
                  type="button"
                  className="support-public__pitch-toggle"
                  aria-expanded={pitchExpanded}
                  onClick={() => setPitchExpanded(expanded => !expanded)}
                >
                  {pitchExpanded ? t('actions.show_less') : t('actions.read_more')}
                </button>
              )}
            </div>
          )}

          {supportedUser?.user_name && (
            <div className="support-public__summary">
              <div className="support-public__summary-avatar">
                <UserProfileImage
                  image={supportedUserProfileImage}
                  typeOfProfile={supportedUserProfileType}
                  className="support-public__summary-avatar-image"
                />
              </div>
              <div className="support-public__summary-text">
                <p className="support-public__summary-title">
                  {frequency === 'monthly' ? t('summary.recurring_support_for') : t('summary.one_time_support_for')}{' '}
                  <strong>{supportedUser.user_name}</strong>
                </p>
                <p className="support-public__summary-subtitle">
                  {supportedUser.user_username ? `@${supportedUser.user_username}` : supportedUser.channel_name ?? ''}
                </p>
                <p className="support-public__summary-meta">
                  {frequencyLabel} · {giveAnonymously ? t('summary.anonymous_gift') : t('summary.public_support')}
                </p>
              </div>
            </div>
          )}

          <div className="support-public__frequency">
            <button
              type="button"
              className={frequency === 'one-time' ? 'active' : ''}
              onClick={() => setFrequency('one-time')}
            >
              {t('summary.one_time')}
            </button>
            <button
              type="button"
              className={frequency === 'monthly' ? 'active' : ''}
              onClick={() => setFrequency('monthly')}
            >
              {t('summary.monthly')}
            </button>
          </div>

          {presetAmounts.length > 0 && (
            <div className="support-public__tiers">
              {presetAmounts.map((amount, index) => (
                <button
                  type="button"
                  key={`preset-${index}`}
                  className={`support-public__preset ${selectedPresetIndex === index ? 'support-public__preset--active' : ''}`}
                  onClick={() => handleSelectPreset(index)}
                >
                  {formatCurrency(amount, 0)}
                </button>
              ))}
            </div>
          )}

          {hasCustomSlot && (
            <div className="support-public__custom">
              <label htmlFor="support-custom-amount">{t('labels.custom_amount')}</label>
              <div
                className={`support-public__custom-input-wrapper${isCustomAmountActive ? ' support-public__custom-input-wrapper--active' : ''}`}
                onClick={() => {
                  handleCustomAmountFocus();
                  customAmountInputRef.current?.focus();
                }}
              >
                {currencySymbolPlacement === 'prefix' && (
                  <span className="support-public__custom-currency">
                    {currencySymbol}
                  </span>
                )}
                <input
                  className="support-public__custom-input"
                  id="support-custom-amount"
                  ref={customAmountInputRef}
                  type="number"
                  min={3}
                  step={1}
                  placeholder="0"
                  value={customAmount}
                  style={{ width: customAmountInputWidth }}
                  onFocus={handleCustomAmountFocus}
                  onChange={handleCustomAmountChange}
                />
                {currencySymbolPlacement === 'suffix' && (
                  <span className="support-public__custom-currency">
                    {currencySymbol}
                  </span>
                )}
              </div>
              {showInlineMinAmountError && (
                <p className="support-public__custom-error">
                  {t('errors.min_amount', { currencySymbol })}
                </p>
              )}
            </div>
          )}

          {displayTiers.length > 0 && (
            <div className="support-public__perks">
              {displayTiers.map(tier => (
                <div
                  key={tier.id}
                  role="button"
                  tabIndex={0}
                  className={`support-public__perks-item${selectedPerkId === tier.id ? ' support-public__perks-item--active' : ''}`}
                  onClick={() => handlePerkSelect(tier.amount, tier.id)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handlePerkSelect(tier.amount, tier.id);
                    }
                  }}
                >
                  <span className="support-public__perks-amount">{formatCurrency(Number(tier.amount || 0), 0)}</span>
                  <div className="support-public__perks-text">
                    {tier.label && <span className="support-public__perks-label">{tier.label}</span>}
                    {tier.note && <span className="support-public__perks-note">{tier.note}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="support-public__benefits">
            <ul className="support-public__benefits-list">
              <li>
                {frequency === 'monthly'
                  ? t('benefits.ai_monthly')
                  : t('benefits.ai_one_time')}
              </li>
            </ul>
          </div>

          <label className="support-public__option">
            <input
              type="checkbox"
              checked={giveAnonymously}
              onChange={event => setGiveAnonymously(event.target.checked)}
            />
            {t('summary.gift_anonymously')}
          </label>

          {goalAmount && config?.goal?.label && (
            <div className="support-public__goal support-preview__goal">
              <span className="support-preview__goal-label">{config.goal.label}</span>
              <div className="support-preview__goal-progress">
                <span className="support-preview__goal-amount">
                  {t('labels.goal_amount', {
                    currencySymbol: '',
                    amount: formatCurrency(goalAmount, 0),
                  })}
                </span>
                <div className="support-preview__goal-bar" role="presentation">
                  <div
                    className="support-preview__goal-bar-fill"
                    style={{ width: `${goalProgressPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {showPaymentDetails && (
            <>
              <div className="support-public__totals">
                <div>
                  <span>{t('totals.support_price')}</span>
                  <strong>{formatCurrency(selectedAmount)}</strong>
                </div>
                <div>
                  <span>{t('totals.vat', { rate: vatPercentLabel.toFixed(0) })}</span>
                  <strong>{formatCurrency(vatAmount)}</strong>
                </div>
                <div>
                  <span>{t('totals.total')}</span>
                  <strong>{formatCurrency(totalAmount)}</strong>
                </div>
              </div>

              {vatLoading && (
                <p className="support-public__vat-note">{t('messages.calculating_vat')}</p>
              )}
              {vatError && (
                <p className="support-public__vat-error">{vatError}</p>
              )}

              {loadingPaymentMethods ? (
                <div className="support-public__payment-loading" role="status" aria-label={t('messages.loading_payment_methods')}>
                  <Loader inline thin />
                </div>
              ) : paymentMethods.length > 0 ? (
                <div className="support-public__payment">
                  <p className="support-public__payment-label">{t('labels.payment_method')}</p>
                  <div className="support-public__payment-list">
                    {paymentMethods.map(method => {
                      const methodId = method.id ?? method.token ?? method.key;
                      const isSelected = methodId === selectedPaymentMethodId;
                      const brandLabel = method.brand ? method.brand.toUpperCase() : t('labels.card').toUpperCase();
                      const title = method.label ?? `${brandLabel} **** ${method.last4 ?? ""}`;
                      const detailLine =
                        method.details ??
                        (method.last4 ? `${brandLabel} **** ${method.last4}` : null);
                      return (
                        <button
                          type="button"
                          key={methodId ?? method.label}
                          className={`support-public__payment-item ${isSelected ? 'active' : ''}`}
                          onClick={() => setSelectedPaymentMethodId(methodId)}
                          title={title}
                        >
                          <div>
                            {(detailLine || title) && (
                              <small>{detailLine || title}</small>
                            )}
                          </div>
                          {isSelected && <span className="support-public__payment-selected">{t('labels.default')}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="support-public__payment-add"
                    onClick={handleAddPaymentMethod}
                    disabled={addingPaymentMethod}
                  >
                    {addingPaymentMethod ? (
                      <Loader inline className="support-public__payment-add-spinner" thin />
                    ) : (
                      t('actions.add_payment_method')
                    )}
                  </button>
                </div>
              ) : (
                <div className="support-public__payment-empty">
                  <div className="support-public__payment-empty-copy">
                    <CreditCardIcon className="support-public__payment-empty-icon" />
                    <div>
                      <p className="support-public__payment-empty-title">{t('labels.payment_method')}</p>
                      <p className="support-public__payment-empty-message">
                        {paymentMethodError || t('messages.no_payment_methods')}
                      </p>
                    </div>
                  </div>
                  <ContinueButton
                    className="support-public__payment-add"
                    onClick={handleAddPaymentMethod}
                    disabled={addingPaymentMethod}
                  >
                    {addingPaymentMethod ? (
                      <Loader inline className="support-public__payment-add-spinner" thin />
                    ) : (
                      t('actions.add_payment_method')
                    )}
                  </ContinueButton>
                </div>
              )}

              <BillingDetailsCard
                className="support-public__billing-card"
                billingAddress={billingAddress}
                billingInfo={billingInfo}
                onSaved={({ billingAddress: nextAddress, billingInfo: nextInfo }) => {
                  setBillingAddress(nextAddress ?? null);
                  setBillingInfo(nextInfo ?? null);
                }}
                triggerOnly
              />
            </>
          )}

          {displayErrorMessage && (
            <p className="support-public__error">{displayErrorMessage}</p>
          )}
        </div>

        <footer className="support-public__footer">
          <ContinueButton
            disabled={
              selectedAmount < 3 ||
              (showPaymentDetails && !selectedPaymentMethod)
            }
            loading={isSubmitting}
            onClick={handlePrimaryAction}>
            {supportedUser?.user_name
              ? t('cta.with_name', {
                name: supportedUser.user_name,
                amount: formatCurrency(ctaAmount >= 3 ? ctaAmount : 0, ctaAmountDigits)
              })
              : t('cta.default', { amount: formatCurrency(ctaAmount >= 3 ? ctaAmount : 0, ctaAmountDigits) })}
          </ContinueButton>
        </footer>
      </div>
    </GenericPopup>
  );
}
